import { useState } from 'react';
import {
  Dialog,
  DropDown,
  FlexColumn,
  TextField,
} from '@elementary/common-ui/components';
import { InputLabel } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { expireOptions } from './constants';
import SuccessStageDialog from './SuccessStageDialog';
import { TokenType } from './types';

type FormData = {
  description: string;
  expiry: string;
};

type DialogStage = 'generate' | 'success';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  tokenType: TokenType;
  hasDescription?: boolean;
  onCreateToken: (data: {
    description: string;
    expiresInMinutes?: number;
    roleIds?: string[];
  }) => Promise<{ secret: string }>;
  isCreating: boolean;
};

const GenerateTokenDialog = ({
  isOpen,
  onClose,
  tokenType,
  hasDescription = true,
  onCreateToken,
  isCreating,
}: Props) => {
  const [dialogStage, setDialogStage] = useState<DialogStage>('generate');
  const [generatedToken, setGeneratedToken] = useState<string>('');

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      description: '',
      expiry: '',
    },
  });

  const onSubmit = async (data: FormData) => {
    const expiresInMinutes =
      data.expiry === 'never' ? undefined : parseInt(data.expiry, 10);
    const token = await onCreateToken({
      description: data.description,
      expiresInMinutes,
    });
    if (token.secret) {
      setGeneratedToken(token.secret);
      setDialogStage('success');
    }
  };

  const handleClose = () => {
    onClose();
    setDialogStage('generate');
    setGeneratedToken('');
    reset();
  };

  if (dialogStage === 'success') {
    return (
      <SuccessStageDialog
        isOpen={isOpen}
        onClose={handleClose}
        token={generatedToken}
        tokenType={tokenType}
      />
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      onConfirmButtonClick={handleSubmit(onSubmit)}
      title="Generate token"
      confirmText="Create"
      confirmDisabled={isCreating || !isValid}
      isLoading={isCreating}
      closeOnBackdropClick={false}
      message={
        <FlexColumn sx={{ gap: 2 }}>
          {hasDescription && (
            <FlexColumn>
              <InputLabel shrink>Description</InputLabel>
              <Controller
                name="description"
                control={control}
                rules={{ required: 'Description is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="large"
                    error={!!errors.description}
                    placeholder="e.g. Test API Token"
                  />
                )}
              />
            </FlexColumn>
          )}
          <FlexColumn>
            <InputLabel shrink>This token will expire</InputLabel>
            <Controller
              name="expiry"
              control={control}
              rules={{ required: 'Expiry is required' }}
              render={({ field }) => (
                <FlexColumn>
                  <DropDown
                    {...field}
                    size="large"
                    options={expireOptions}
                    sortOptions={false}
                    placeholder="Select expiry option"
                    error={!!errors.expiry}
                  />
                </FlexColumn>
              )}
            />
          </FlexColumn>
        </FlexColumn>
      }
    />
  );
};

export default GenerateTokenDialog;
